"use client";
import { API_URL } from "@/lib/config";

// Phase 2: Cmd+K global search palette backed by GET /api/search (Meilisearch).
// Mount once at the app root. On Cmd+K (or Ctrl+K) the overlay opens.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, Map, Loader2, AlertTriangle } from "lucide-react";

const API = API_URL;

interface SearchHit {
  id: string;
  name: string;
  creator_name: string;
  username: string;
  view_count: number;
  status: string;
  city?: string;
  country?: string;
  _formatted?: { name?: string; creator_name?: string };
}

interface GlobalSearchPaletteProps {
  onOpenPanorama: (id: string) => void;
}

export default function GlobalSearchPalette({ onOpenPanorama }: GlobalSearchPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Open on Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery("");
      setHits([]);
      setSelectedIdx(0);
    }
  }, [open]);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setHits([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API}/api/search?q=${encodeURIComponent(q)}&limit=12`,
        { credentials: "include" }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setHits(data.hits ?? []);
      setSelectedIdx(0);
    } catch (e: any) {
      setError("Search unavailable");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 280);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && hits[selectedIdx]) {
      openHit(hits[selectedIdx]);
    }
  };

  const openHit = (hit: SearchHit) => {
    setOpen(false);
    onOpenPanorama(hit.id);
  };

  // Highlight helper — Meilisearch puts highlighted text in _formatted
  const hl = (hit: SearchHit, field: "name" | "creator_name"): string =>
    hit._formatted?.[field] ?? hit[field] ?? "";

  return (
    <>
      {/* Trigger button (optional — keyboard shortcut works too) */}
      <button
        onClick={() => setOpen(true)}
        className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-ink/5 hover:bg-ink/10 border border-ink/10 text-sm text-tone-400 hover:text-tone-200 transition-colors"
        aria-label="Open global search (⌘K)"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Search</span>
        <kbd className="ml-1 text-[10px] px-1.5 py-0.5 bg-ink/8 rounded border border-ink/10 font-mono">⌘K</kbd>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[400] flex items-start justify-center pt-[12vh] px-4"
            onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

            {/* Palette */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -10 }}
              transition={{ duration: 0.15 }}
              className="relative w-full max-w-xl bg-tone-900 border border-ink/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-ink/8">
                <Search className="w-4 h-4 text-tone-500 shrink-0" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Search panoramas, places, creators…"
                  className="flex-1 bg-transparent text-sm text-ink placeholder-tone-500 outline-none"
                />
                {loading && <Loader2 className="w-4 h-4 animate-spin text-indigo-400 shrink-0" />}
                <button
                  onClick={() => setOpen(false)}
                  className="p-1 rounded-lg text-tone-600 hover:text-tone-300 hover:bg-ink/8 transition-colors shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Results */}
              <div className="max-h-80 overflow-y-auto">
                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 text-sm text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                {!error && hits.length === 0 && query.trim() && !loading && (
                  <div className="px-4 py-8 text-center text-sm text-tone-500">
                    No results for &ldquo;{query}&rdquo;
                  </div>
                )}

                {!error && hits.length === 0 && !query.trim() && (
                  <div className="px-4 py-6 text-center text-xs text-tone-600">
                    Type to search all panoramas
                  </div>
                )}

                {hits.map((hit, idx) => (
                  <button
                    key={hit.id}
                    onClick={() => openHit(hit)}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      idx === selectedIdx ? "bg-indigo-500/15" : "hover:bg-ink/5"
                    }`}
                  >
                    <Map className="w-4 h-4 text-tone-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-semibold text-tone-100 truncate"
                        dangerouslySetInnerHTML={{ __html: hl(hit, "name") || "Untitled" }}
                      />
                      <p className="text-xs text-tone-500 truncate">
                        <span dangerouslySetInnerHTML={{ __html: hl(hit, "creator_name") }} />
                        {(hit.city || hit.country) && (
                          <> · {[hit.city, hit.country].filter(Boolean).join(", ")}</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 text-xs text-tone-600">
                      <span>{hit.view_count.toLocaleString()} views</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-2 border-t border-ink/5 text-[10px] text-tone-600">
                <span>↑↓ navigate · Enter open · Esc close</span>
                <span>Powered by Meilisearch</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
